// Omnix/client/ui/ContactManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class ContactManager extends ComponentBase {
    static styles = `
        .contact-manager-container {
            display: flex;
            height: 100%;
            overflow: hidden;
            background-color: var(--bs-body-bg);
        }
        .contact-index-pane {
            width: 350px;
            min-width: 300px;
            border-right: 1px solid var(--bs-border-color);
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
        }
        .contact-detail-pane {
            flex-grow: 1;
        }
        /* Mobile Responsive Layout */
        @media (max-width: 768px) {
            .contact-manager-container.viewing-detail .contact-index-pane {
                display: none;
            }
            .contact-manager-container:not(.viewing-detail) .contact-detail-pane {
                display: none;
            }
            .contact-index-pane, .contact-detail-pane {
                width: 100%;
                border-right: none;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.contacts = [];
        this.allUsers = []; // To map createdBy ID to username
        this.selectedContact = null;
        this.isDetailViewVisible = false;

        // Bound handlers for event listeners
        this._boundHandleContactSelected = this.handleContactSelected.bind(this);
        this._boundHandleNewContact = this.handleNewContact.bind(this);
        this._boundHandleEditContact = this.handleEditContact.bind(this);
        this._boundHandleContactSaved = this.handleContactSaved.bind(this);
        this._boundHandleCloseDetail = this.handleCloseDetail.bind(this);

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'contact-manager-container';

        // Create child component instances
        this.indexView = await this.createManagedComponent('ContactIndexView', null, { instanceId: `${this.config.instanceId}-index` });
        this.detailView = await this.createManagedComponent('ContactDetailView', null, { instanceId: `${this.config.instanceId}-detail` });
        this.contactForm = await this.createManagedComponent('ContactForm', null, { instanceId: `${this.config.instanceId}-form` });
        this.mergeModal = await this.createManagedComponent('MergeContactsModal', null, { instanceId: `${this.config.instanceId}-merge-modal` });

        // Attach event listeners
        this.indexView.on('contactSelected', this._boundHandleContactSelected);
        this.indexView.on('newContactClicked', this._boundHandleNewContact);
        this.detailView.on('editContactClicked', this._boundHandleEditContact);
        this.detailView.on('closeDetail', this._boundHandleCloseDetail);
        this.detailView.on('deleteContactClicked', this.handleDeleteContact.bind(this));
        this.detailView.on('mergeContactClicked', this.handleMergeContact.bind(this));
        this.mergeModal.on('contactsMerged', this._boundHandleContactSaved); // Refresh list after merge

        this.contactForm.on('contactSaved', this._boundHandleContactSaved);
        this.indexView.on('refreshContacts', this._boundHandleContactSaved); // Re-use the existing data loading logic

        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadInitialData();
    }

    async loadInitialData() {
        try {
            // Fetch contacts and users in parallel
            const [contactsResult, usersResult] = await Promise.all([
                this.appClient.rpcClient.call('OmniContactsRpc.getData', { pageSize: 1000 }),
                this.appClient.rpcClient.call('UserManagerRpc.getData', { pageSize: 1000 }) // For createdBy mapping
            ]);
            this.contacts = contactsResult.data || [];
            this.allUsers = usersResult.data || [];

            // Pass data to child components
            this.indexView.setContacts(this.contacts);
            this.detailView.setAllUsers(this.allUsers);

        } catch (error) {
            this.log.error("Failed to load initial contact data:", error);
            this.appClient.notifier.error("Could not load contacts.");
        }
    }

    handleContactSelected(contact) {
        this.selectedContact = contact;
        this.isDetailViewVisible = true;
        this.detailView.setContact(contact);
        this.updateLayout();
    }

    handleNewContact() {
        this.contactForm.show('create');
    }

    handleEditContact(contact) {
        this.contactForm.show('edit', contact);
    }

    async handleContactSaved() {
        // Refresh the list after a contact is created or updated
        await this.loadInitialData();
        // If a contact was being edited, refresh its detail view
        if (this.selectedContact) {
            const updatedContact = this.contacts.find(c => c._id === this.selectedContact._id);
            if (updatedContact) {
                this.detailView.setContact(updatedContact);
            } else {
                // The contact might have been deleted or is no longer visible
                this.handleCloseDetail();
            }
        }
    }

    async handleDeleteContact(contact) {
        const confirmed = await this.appClient.notifier.confirm(`Are you sure you want to delete "${contact.firstName}"? This will also remove them from any conversations.`, { type: 'danger' });
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('OmniContactsRpc.delete', { _id: contact._id });
                this.appClient.notifier.success("Contact deleted.");
                this.handleCloseDetail(); // Close the detail pane
                this.loadInitialData(); // Refresh the index
            } catch (error) {
                this.appClient.notifier.error(`Delete failed: ${error.message}`);
            }
        }
    }

    handleMergeContact(contact) {
        this.mergeModal.show(contact);
    }


    handleCloseDetail() {
        this.selectedContact = null;
        this.isDetailViewVisible = false;
        this.detailView.setContact(null); // Clear the detail view
        this.updateLayout();
    }

    updateLayout() {
        if (this.isDetailViewVisible) {
            this.container.classList.add('viewing-detail');
        } else {
            this.container.classList.remove('viewing-detail');
        }
    }

    async render() {
        this.container.innerHTML = ''; // Clear before rendering

        const indexPane = document.createElement('div');
        indexPane.className = 'contact-index-pane';
        indexPane.appendChild(await this.indexView.render());

        const detailPane = document.createElement('div');
        detailPane.className = 'contact-detail-pane';
        detailPane.appendChild(await this.detailView.render());

        this.container.appendChild(indexPane);
        this.container.appendChild(detailPane);

        // The form is a modal and will be appended to the body by its own logic
        this.container.appendChild(await this.contactForm.render());
        this.container.appendChild(await this.mergeModal.render());

        return this.container;
    }

     async refresh() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        const contactIdFromUrl = queryParams.contactId;
        
        // Ensure data is loaded before trying to select
        if (this.contacts.length === 0) {
            await this.loadInitialData();
        }

        if (contactIdFromUrl) {
            const contactToSelect = this.contacts.find(c => c._id === contactIdFromUrl);
            if (contactToSelect) {
                this.log.info(`ContactManager shown with contactId in URL: ${contactIdFromUrl}. Selecting contact.`);
                // This will trigger both the index and detail views to update
                this.indexView.selectedContactId = contactIdFromUrl;
                this.indexView.renderContactList(); // Re-render list to show active state
                this.handleContactSelected(contactToSelect);
            }
        }
    }

    destroy() {
        // Unbind listeners
        this.indexView?.off('contactSelected', this._boundHandleContactSelected);
        this.indexView?.off('newContactClicked', this._boundHandleNewContact);
        this.detailView?.off('editContactClicked', this._boundHandleEditContact);
        this.detailView?.off('closeDetail', this._boundHandleCloseDetail);
        this.contactForm?.off('contactSaved', this._boundHandleContactSaved);

        this.detailView?.off('deleteContactClicked', this.handleDeleteContact);
        this.detailView?.off('mergeContactClicked', this.handleMergeContact);
        this.mergeModal?.off('contactsMerged', this._boundHandleContactSaved);

        // Destroy managed components
        this.indexView?.destroy();
        this.detailView?.destroy();
        this.contactForm?.destroy();

        super.destroy();
    }
}